import {
  AgxButton,
  AgxModal,
  AgxSpacer,
  AgxTextArea,
  AgxTextInput,
  Breakpoints,
  useWindowSize,
} from '@urbanx/agx.ui-components2';
import { useState } from 'react';
import type { AgencySupplier } from '@urbanx/agx.agencies.client';

interface SupplierUpdateModalProps {
  isEditMode?: boolean;
  updateableData: AgencySupplier;
  onSave: (supplier: AgencySupplier) => void;
  onDelete: (supplierId: string | null) => void;
  onClose: () => void;
}

const SupplierUpdateModal = ({
  isEditMode,
  updateableData: { ...supplier },
  onSave,
  onDelete,
  onClose,
}: SupplierUpdateModalProps) => {
  const [currentSupplier, setSupplier] = useState<AgencySupplier>(supplier);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [shouldValidate, setShouldValidate] = useState({
    companyName: false,
    email: false,
    phone: false,
  });
  const isMobile = useWindowSize() === Breakpoints.Mobile;

  const cellIds = {
    companyName: 'companyName',
    email: 'email',
    phone: 'phone',
    notes: 'notes',
  };

  const modalWidth = `${isMobile ? 370 : 500}px`;

  const onSupplierUpdate = ({ id, value }: { id: string; value: string }) => {
    let updatedSupplier = { ...currentSupplier };

    switch (id) {
      case cellIds.companyName:
        updatedSupplier = {
          ...updatedSupplier,
          companyName: value,
        };
        break;
      case cellIds.email:
        updatedSupplier = {
          ...updatedSupplier,
          email: { address: value },
        };
        break;
      case cellIds.phone:
        updatedSupplier = {
          ...updatedSupplier,
          phone: { number: value },
        };
        break;
      case cellIds.notes:
        updatedSupplier = {
          ...updatedSupplier,
          notes: value,
        };
        break;
      default:
        console.log(`Supplier field ${value} could not be found`);
    }

    setSupplier(updatedSupplier);
  };

  const onSaveClick = () => {
    setShouldValidate({
      companyName: !currentSupplier?.companyName,
      email: !currentSupplier?.email?.address,
      phone: !currentSupplier?.phone?.number,
    });

    if (
      !currentSupplier?.companyName ||
      !currentSupplier?.email?.address ||
      !currentSupplier?.phone?.number
    )
      return;

    onSave(currentSupplier);
  };

  return (
    <>
      <AgxModal
        title={isEditMode ? 'Edit Supplier' : 'Add Supplier'}
        isOpen={!confirmDelete}
        type="single"
        variant="withDivider"
        actionButtonContainerStyle="withDivider"
        minWidth={modalWidth}
        setIsDialogOpen={(isOpen) => {
          if (!isOpen) onClose();
        }}
        actionButtons={
          <AgxSpacer direction="horizontal" contentJustification="center" gap="spacing-lg">
            <AgxButton
              id="supplier-save-changes"
              text="Save Changes"
              variant="primary"
              size="large"
              onClick={onSaveClick}
            />
            <AgxButton
              id={isEditMode ? 'supplier-delete' : 'supplier-cancel'}
              text={isEditMode ? 'Delete' : 'Cancel'}
              variant="hollow"
              size="large"
              onClick={() => {
                isEditMode ? setConfirmDelete(true) : onClose();
              }}
            />
          </AgxSpacer>
        }
        content={
          <AgxSpacer
            direction="vertical"
            gap="spacing-lg"
            fill
            padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
          >
            <AgxTextInput
              id={cellIds.companyName}
              type="text"
              required
              stretch
              label="Company Name"
              hideOptionalText
              defaultValue={supplier?.companyName}
              errorText={shouldValidate.companyName ? 'Company name required' : ''}
              onInputValueChange={(data) => onSupplierUpdate(data)}
            />
            <AgxTextInput
              id={cellIds.email}
              type="email"
              required
              stretch
              label="Email"
              hideOptionalText
              defaultValue={supplier?.email?.address}
              errorText={shouldValidate.email ? 'Email required' : ''}
              onInputValueChange={(data) => onSupplierUpdate(data)}
            />
            <AgxTextInput
              id={cellIds.phone}
              type="phone"
              required
              stretch
              label="Phone"
              hideOptionalText
              defaultValue={supplier?.phone?.number}
              errorText={shouldValidate.phone ? 'Phone required' : ''}
              onInputValueChange={(data) => onSupplierUpdate(data)}
            />
            <AgxTextArea
              id={cellIds.notes}
              stretch
              noResize
              maxLength={100}
              rows={3}
              label="Notes"
              hideOptionalText
              placeholder="Enter notes here"
              defaultValue={supplier?.notes}
              onInputValueChange={(data) => onSupplierUpdate(data)}
            />
          </AgxSpacer>
        }
      />
      <AgxModal
        title={`Are you sure you want to delete ${currentSupplier.companyName ?? 'this supplier'}?`}
        isOpen={confirmDelete}
        type="dialog"
        variant="withDivider"
        actionButtonContainerStyle="withoutDivider"
        minWidth={modalWidth}
        setIsDialogOpen={(isOpen) => {
          if (!isOpen) setConfirmDelete(false);
        }}
        actionButtons={
          <AgxSpacer
            direction="horizontal"
            contentJustification="center"
            gap="spacing-lg"
            padding={{
              top: 'sm',
            }}
          >
            <AgxButton
              id="confirm-supplier-delete"
              text="Delete"
              variant="primary"
              size="large"
              onClick={() => onDelete(currentSupplier?.supplierId ?? null)}
            />
            <AgxButton
              id="cancel-supplier-delete"
              text="Cancel"
              variant="hollow"
              size="large"
              onClick={() => setConfirmDelete(false)}
            />
          </AgxSpacer>
        }
        content={null}
      />
    </>
  );
};

export default SupplierUpdateModal;
